import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CacheKeys } from "../../constants/CacheKeys";
import { VsaTableColumnDefinition } from "../../constants/DataGridColumns";
import { SelectionPersistenceKeys } from "../../constants/SelectionPersistenceKeys";
import { useGetActivityFields } from "../../hooks/useActivities";
import { Activity } from "../../models/Activity";
import { ApiEndpoints } from "../../models/ApiEndpoints";
import { ClientRoutes } from "../../models/ClientRoutes";
import { ReportType } from "../../services/ReportService";
import { getActivityColumnsFromColumnOrder } from "../../services/ViewService";
import { MassCreateDraftButton } from "../list/MassCreateDraftButton";
import { PaginatedList } from "../list/PaginatedList";

export const PublishedActivityList = () => {
  const { data: viewDefinition, isLoading: viewDefinitionLoading } =
    useGetActivityFields();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const onMassCreateDraftSuccess = (createdVersionIds: string[]) => {
    window.sessionStorage.setItem(
      SelectionPersistenceKeys.ActivitiesDrafts,
      JSON.stringify(createdVersionIds)
    );
    navigate(ClientRoutes.ActivitiesDrafts);
  };

  const columns = useMemo<VsaTableColumnDefinition<Activity>[]>(() => {
    if (viewDefinitionLoading || !viewDefinition) {
      return [];
    }

    return getActivityColumnsFromColumnOrder(
      viewDefinition.standardFields,
      viewDefinition.fields,
      viewDefinition.publishedFieldOrder,
      false
    );
  }, [viewDefinition, viewDefinitionLoading]);

  return (
    <PaginatedList<Activity>
      apiEndpoint={ApiEndpoints.ActivitiesCurrent}
      columns={columns}
      defaultSortAscending={false}
      defaultSortColumnName={"modified"}
      searchPlaceholder={t("searchActivity")}
      cacheKey={CacheKeys.Activity.ActivityList}
      filtersEndpoint={ApiEndpoints.ActivityFilters}
      reportType={ReportType.PublishedActivities}
      storageKey="activities:published"
      libraryFilterScope={{ listType: "activity" }}
      selectedActions={(selectedVersionIds) => (
        <MassCreateDraftButton
          versionIds={selectedVersionIds}
          entityType="activity"
          onSuccess={onMassCreateDraftSuccess}
        />
      )}
    />
  );
};
